// ASPETTO — porto sul web lo stesso motore dei temi dell'app mobile (src/lib/appearance.js).
// Stessi sei accenti, stessi tre fondi scuri, stesse chiavi. Chi cambia tema sul telefono
// ritrova la stessa scelta nel browser perche' i valori sono identici, non «simili».
//
// I colori escono come variabili CSS su :root, cosi' ogni componente puo' scriverli in
// Tailwind con `text-[var(--lt-primary)]` e cambiano tutti nello stesso fotogramma.
import { useEffect, useState } from "react";

const KEY = "lat.look.v1";

export const ACCENTS = [
  { id: "smeraldo", label: "Verde Smeraldo", labelEn: "Emerald Green", color: "#50C878", deep: "#1E5637" },
  { id: "blu", label: "Blu Elettrico", labelEn: "Electric Blue", color: "#3D8BFF", deep: "#123A7A" },
  { id: "oro", label: "Giallo Oro", labelEn: "Gold", color: "#E8B33C", deep: "#6B4E12" },
  { id: "rubino", label: "Rosso Rubino", labelEn: "Ruby Red", color: "#E0475F", deep: "#6B1622" },
  { id: "arancio", label: "Arancione Vivace", labelEn: "Vivid Orange", color: "#FF7A2F", deep: "#7A3410" },
  { id: "argento", label: "Bianco Argento", labelEn: "Silver White", color: "#DCE3E8", deep: "#4A5158" },
];

export const BACKGROUNDS = [
  { id: "nero", label: "Deep Black", color: "#000000", surface: "#0A0A0A", surfaceAlt: "#121212", border: "#1C1C1C" },
  { id: "blunotte", label: "Midnight Blue", color: "#00040F", surface: "#070C1C", surfaceAlt: "#0C1428", border: "#161F38" },
  { id: "foresta", label: "Forest Green", color: "#00110A", surface: "#061A10", surfaceAlt: "#0A2417", border: "#123522" },
];

let current = { accent: "smeraldo", bg: "nero" };
const listeners = new Set();

export const accent = () => ACCENTS.find((a) => a.id === current.accent) || ACCENTS[0];
export const background = () => BACKGROUNDS.find((b) => b.id === current.bg) || BACKGROUNDS[0];
export const get = () => current;
export const accentColor = () => accent().color;

function hexRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)].join(" ");
}

function apply() {
  if (typeof document === "undefined") return;
  const a = accent();
  const b = background();
  const r = document.documentElement.style;
  r.setProperty("--lt-primary", a.color);
  r.setProperty("--lt-primary-rgb", hexRgb(a.color));
  r.setProperty("--lt-primary-deep", a.deep);
  r.setProperty("--lt-bg", b.color);
  r.setProperty("--lt-surface", b.surface);
  r.setProperty("--lt-surface-alt", b.surfaceAlt);
  r.setProperty("--lt-border", b.border);
  // Sull'argento il testo nero sul pieno accento e' l'unica combinazione leggibile.
  r.setProperty("--lt-on-primary", a.id === "argento" || a.id === "oro" ? "#000000" : "#000000");
}

function notify() {
  apply();
  listeners.forEach((f) => { try { f(current); } catch { /* niente */ } });
}

export function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) current = Object.assign({}, current, JSON.parse(raw));
  } catch { /* si tengono i valori di partenza */ }
  apply();
  return current;
}

export function set(patch) {
  current = Object.assign({}, current, patch || {});
  try { localStorage.setItem(KEY, JSON.stringify(current)); } catch { /* niente */ }
  notify();
  return current;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/// Colore d'accento attivo, con ridisegno automatico quando cambia.
export function useTint() {
  const [c, setC] = useState(accentColor());
  useEffect(() => {
    setC(accentColor());
    return subscribe(() => setC(accentColor()));
  }, []);
  return c;
}

/// Scelta completa: accento, fondo e superfici calibrate.
export function useLook() {
  const calc = () => ({ ...current, ...background(), tint: accent().color, deep: accent().deep });
  const [v, setV] = useState(calc);
  useEffect(() => {
    setV(calc());
    return subscribe(() => setV(calc()));
  }, []);
  return v;
}

load();
